import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { logEvent } from "firebase/analytics";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import { setSuccessToast } from "@/helpers/notifications/toast";
import {
  copyToClipboardFlutter,
  shareWithFlutter,
} from "@/helpers/flutter/features/sharing";
import {
  isRunningInFlutter,
  hasSupportForFlutterShare,
} from "@/helpers/flutter/capabilities";
import type { ShareOptions } from "@/types/share";

/**
 * Composable for handling clipboard and sharing functionality
 * Works with both web and Flutter environments
 */
export function useClipboardAndShare() {
  const { t } = useI18n();
  const canShare = ref(false);

  // Check if share API is available
  if (typeof navigator.share === "function") {
    canShare.value = true;
  }

  /**
   * Copy text to clipboard with platform-specific handling
   */
  function copyToClipboard(
    text: string,
    successMessageKey: string,
    analyticsEvent?: string,
  ): void {
    if (analyticsEvent) {
      logEvent(firebaseAnalytics, analyticsEvent, { name: "copy" });
    }

    if (isRunningInFlutter()) {
      copyToClipboardFlutter(text, successMessageKey);
    } else {
      navigator.clipboard.writeText(text);
      setSuccessToast({
        message: t(successMessageKey),
      });
    }
  }

  /**
   * Share content using platform-specific share functionality
   * Falls back to copying if sharing is not available
   */
  async function shareContent(
    url: string,
    options: ShareOptions,
    analyticsEvent?: string,
  ): Promise<void> {
    if (hasSupportForFlutterShare()) {
      shareWithFlutter({
        title: url,
        subject: options.subject,
      });
    } else if (canShare.value) {
      if (analyticsEvent) {
        logEvent(firebaseAnalytics, analyticsEvent, { name: "share" });
      }

      try {
        await navigator.share({
          title: options.title,
          url: url,
          text: options.subject,
        });
      } catch {
        // If share fails, fall back to copying
        canShare.value = false;
        copyToClipboard(url, "global.toast.link_copied", analyticsEvent);
      }
    } else {
      copyToClipboard(url, "global.toast.link_copied", analyticsEvent);
    }
  }

  return {
    canShare,
    copyToClipboard,
    shareContent,
  };
}
